import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { MdAdd, MdCampaign, MdContentCopy, MdDeleteForever, MdDescription, MdEdit, MdSchedule, MdSearch, MdSend, MdSms, MdVisibility } from 'react-icons/md';
import { getData, postData, putData, deleteData } from '../../../Webservice/Webservice';
import SmsForm from './SmsForm';
import { FONT, PhoneBubble, Progress, SMS_STATUS, SmsChip, authToken, detectVariables, fmtDateTime, smsParts } from './smsShared';

const iconBtn = (color, bg) => ({ color, background: bg, borderRadius: '8px', '&:hover': { background: bg, filter: 'brightness(.95)' } });
const EMPTY_TEMPLATE = { name: '', templateId: '', content: '', variables: '' };

function SMS() {
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [copySource, setCopySource] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  // template dialog (add + edit)
  const [tplDialog, setTplDialog] = useState({ open: false, editingId: '', data: EMPTY_TEMPLATE, errors: {} });
  const [tplDelete, setTplDelete] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadCampaigns = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const res = await getData('sms-campaign/list', null, authToken(), false);
      setCampaigns(res?.campaigns || []);
      setError('');
    } catch (e) {
      if (!quiet) setError('Failed to load SMS campaigns. Please try again.');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);
  const loadTemplates = useCallback(async () => {
    try {
      const res = await getData('sms-campaign/templates', null, authToken(), false);
      setTemplates(res?.templates || []);
    } catch (e) {
      setTemplates([]);
    }
  }, []);

  useEffect(() => { loadCampaigns(); loadTemplates(); }, [loadCampaigns, loadTemplates]);

  const live = campaigns.some((c) => ['running', 'pending'].includes(c.status));
  useEffect(() => {
    if (!live || showForm) return undefined;
    const t = setInterval(() => loadCampaigns(true), 8000);
    return () => clearInterval(t);
  }, [live, showForm, loadCampaigns]);

  const duplicate = useCallback(async (id) => {
    try {
      const res = await getData(`sms-campaign/${id}`, { limit: 1 }, authToken(), false);
      if (!res?.campaign) throw new Error('missing');
      setCopySource(res.campaign);
      setShowForm(true);
    } catch (e) {
      setError('Could not load that campaign to duplicate it.');
    }
  }, []);

  // arriving from a campaign page with "Duplicate" pressed
  useEffect(() => {
    const id = location.state?.duplicateId;
    if (id) {
      navigate(location.pathname, { replace: true, state: null });
      duplicate(id);
    }
  }, [location.state, location.pathname, navigate, duplicate]);

  // ---------- templates ----------
  const openTemplate = (t) => setTplDialog({
    open: true,
    editingId: t?._id || '',
    data: t ? { name: t.name, templateId: t.templateId, content: t.content || '', variables: (t.variables || []).join(', ') } : EMPTY_TEMPLATE,
    errors: {},
  });
  const setTplField = (patch) => setTplDialog((d) => ({ ...d, data: { ...d.data, ...patch }, errors: {} }));

  const saveTemplate = async (e) => {
    e.preventDefault();
    const { data, editingId } = tplDialog;
    const errors = {};
    if (!data.name.trim()) errors.name = 'Give the template a name';
    if (!data.templateId.trim()) errors.templateId = 'The MSG91 template ID is required';
    if (Object.keys(errors).length) return setTplDialog((d) => ({ ...d, errors }));
    setBusy(true);
    try {
      const payload = {
        name: data.name.trim(),
        templateId: data.templateId.trim(),
        content: data.content,
        variables: data.variables.split(',').map((v) => v.trim()).filter(Boolean),
      };
      if (editingId) await putData(`sms-campaign/templates/${editingId}`, payload, authToken());
      else await postData('sms-campaign/templates', payload, authToken());
      setTplDialog((d) => ({ ...d, open: false }));
      await loadTemplates();
    } catch (err) {
      /* shown by the service */
    } finally {
      setBusy(false);
    }
  };

  const removeTemplate = async () => {
    if (!tplDelete) return;
    setBusy(true);
    try {
      await deleteData(`sms-campaign/templates/${tplDelete._id}`, authToken());
      await loadTemplates();
    } catch (err) {
      setError('Could not remove the template.');
    } finally {
      setBusy(false);
      setTplDelete(null);
    }
  };

  const kpis = useMemo(() => {
    const sum = (k) => campaigns.reduce((a, c) => a + (c.stats?.[k] || 0), 0);
    return [
      { label: 'Campaigns', value: campaigns.length, icon: MdCampaign, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', filter: '' },
      { label: 'Sending now', value: campaigns.filter((c) => c.status === 'running').length, icon: MdSend, gradient: 'linear-gradient(135deg,#10b981,#059669)', filter: 'running' },
      { label: 'Scheduled', value: campaigns.filter((c) => c.status === 'scheduled').length, icon: MdSchedule, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)', filter: 'scheduled' },
      { label: 'SMS delivered', value: sum('sent').toLocaleString('en-IN'), icon: MdSms, gradient: 'linear-gradient(135deg,#f59e0b,#ea580c)', filter: 'completed' },
    ];
  }, [campaigns]);

  const rows = campaigns.filter((c) => {
    if (statusFilter && c.status !== statusFilter) return false;
    const q = search.trim().toLowerCase();
    return !q || `${c.name} ${c.campId || ''} ${c.templateId?.name || ''}`.toLowerCase().includes(q);
  });

  if (showForm) {
    return <SmsForm key={copySource?._id || 'new'} templates={templates} source={copySource} onBack={() => { setShowForm(false); setCopySource(null); }} onCreated={() => { setShowForm(false); setCopySource(null); loadCampaigns(); }} />;
  }

  const tplVars = detectVariables(tplDialog.data.content);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, background: '#f8fafc', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 25, color: '#0f172a' }}>SMS Campaigns</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>Send approved template messages to your customers and track delivery.</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button variant="contained" startIcon={<MdAdd />} onClick={() => { setCopySource(null); setShowForm(true); }} sx={{ fontFamily: FONT, background: 'rgb(255 209 2)', color: 'black', textTransform: 'none', borderRadius: '10px', fontWeight: 700, px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', border: '1px solid black', '&:hover': { background: 'rgb(255 209 2)', transform: 'translateY(-1px)' } }}>New Campaign</Button>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 2, mb: 3 }}>
          {kpis.map((k) => (
            <Paper key={k.label} elevation={0} onClick={() => { setTab(0); setStatusFilter(k.filter); }} sx={{ p: 2.5, borderRadius: '16px', color: '#fff', background: k.gradient, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', outline: statusFilter === k.filter && k.filter ? '3px solid #0f172a' : 'none', outlineOffset: 2, boxShadow: '0 4px 12px rgba(15,23,42,.1)', transition: 'transform .15s', '&:hover': { transform: 'translateY(-2px)' } }}>
              <Box sx={{ background: 'rgba(255,255,255,.22)', borderRadius: '14px', p: 1.5, display: 'flex' }}><k.icon size={26} /></Box>
              <Box>
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, opacity: 0.92, fontFamily: FONT }}>{k.label}</Typography>
                <Typography sx={{ fontSize: 25, fontWeight: 800, lineHeight: 1.2, fontFamily: FONT }}>{k.value}</Typography>
              </Box>
            </Paper>
          ))}
        </Box>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, '& .MuiTab-root': { fontFamily: FONT, fontWeight: 800, textTransform: 'none', fontSize: 13.5 }, '& .MuiTabs-indicator': { background: 'rgb(255 190 0)', height: 3 } }}>
          <Tab label={`Campaigns (${campaigns.length})`} />
          <Tab label={`Templates (${templates.length})`} />
        </Tabs>

        {tab === 0 ? (
          <>
            <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: '14px', border: '1px solid #e2e8f0', display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField size="small" placeholder="Search name, campaign ID or template" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 240 }} InputProps={{ startAdornment: <MdSearch size={20} style={{ marginRight: 8, color: '#94a3b8' }} /> }} />
              <TextField select size="small" label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 160 }}>
                <MenuItem value="">All</MenuItem>
                {Object.entries(SMS_STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
              </TextField>
              {(search || statusFilter) && <Button onClick={() => { setSearch(''); setStatusFilter(''); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Clear</Button>}
            </Paper>

            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(15,23,42,.05)' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ '& th': { backgroundColor: '#f8fafc', fontWeight: 800, py: 1.75, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569', fontFamily: FONT, borderBottom: '2px solid #e2e8f0' } }}>
                    <TableCell>Campaign</TableCell>
                    <TableCell>Template</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Failed</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => <TableRow key={i}>{[200, 150, 150, 50, 90, 110].map((w, j) => <TableCell key={j}><Skeleton width={w} /></TableCell>)}</TableRow>)
                  ) : rows.length === 0 ? (
                    <TableRow><TableCell colSpan={6} sx={{ py: 6, textAlign: 'center' }}><Typography sx={{ fontFamily: FONT, color: '#64748b', fontWeight: 600 }}>{campaigns.length ? 'No campaigns match your filters.' : 'No SMS campaigns yet. Create your first one.'}</Typography></TableCell></TableRow>
                  ) : rows.map((c) => (
                    <TableRow key={c._id} hover onClick={() => navigate(`/sms/${c._id}`)} sx={{ cursor: 'pointer', '& td': { borderBottom: '1px solid #f1f5f9', py: 1.5, fontFamily: FONT } }}>
                      <TableCell>
                        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13, color: '#0f172a' }}>{c.name}</Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#64748b' }}>{c.campId ? `${c.campId} · ` : ''}{c.status === 'scheduled' ? `Goes out ${fmtDateTime(c.scheduledAt)}` : `Created ${fmtDateTime(c.createdAt)}`}</Typography>
                      </TableCell>
                      <TableCell><Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#475569' }}>{c.templateId?.name || 'N/A'}</Typography></TableCell>
                      <TableCell><Progress stats={c.stats} status={c.status} /></TableCell>
                      <TableCell><Typography sx={{ fontFamily: FONT, fontWeight: 800, color: c.stats?.failed ? '#dc2626' : '#94a3b8' }}>{c.stats?.failed || 0}</Typography></TableCell>
                      <TableCell><SmsChip status={c.status} /></TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Duplicate" arrow><IconButton size="small" onClick={() => duplicate(c._id)} sx={{ ...iconBtn('#0369a1', '#e0f2fe'), mr: 0.75 }}><MdContentCopy size={16} /></IconButton></Tooltip>
                        <Tooltip title="Track" arrow><IconButton size="small" onClick={() => navigate(`/sms/${c._id}`)} sx={iconBtn('#4f46e5', 'rgba(79,70,229,.1)')}><MdVisibility size={17} /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <Grid container spacing={2.5}>
            {templates.map((t) => (
              <Grid item xs={12} md={6} xl={4} key={t._id}>
                <Paper elevation={0} sx={{ p: 2.5, height: '100%', borderRadius: '16px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 1.5, boxShadow: '0 4px 16px rgba(15,23,42,.05)' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, color: '#0f172a' }}>{t.name}</Typography>
                      <Typography sx={{ fontFamily: 'monospace', fontSize: 11, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis' }} title={t.templateId}>{t.templateId}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
                      <Tooltip title="Edit" arrow><IconButton size="small" onClick={() => openTemplate(t)} sx={iconBtn('#ca8a04', '#fefce8')}><MdEdit size={16} /></IconButton></Tooltip>
                      <Tooltip title="Remove" arrow><IconButton size="small" onClick={() => setTplDelete(t)} sx={iconBtn('#dc2626', 'rgba(220,38,38,.1)')}><MdDeleteForever size={18} /></IconButton></Tooltip>
                    </Box>
                  </Box>
                  <PhoneBubble text={t.content} empty="No preview text added" />
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                    {(t.variables || []).length ? t.variables.map((v) => <Chip key={v} label={`##${v}##`} size="small" sx={{ fontFamily: 'monospace', fontSize: 11.5, background: '#eef2ff', color: '#3730a3' }} />) : <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#94a3b8' }}>No variables</Typography>}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Template add / edit */}
      <Dialog open={tplDialog.open} onClose={() => !busy && setTplDialog((d) => ({ ...d, open: false }))} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden' } }}>
        <Box component="form" onSubmit={saveTemplate}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2.25, background: 'linear-gradient(135deg, #6366f11f, #6366f108)', borderBottom: '3px solid #6366f1' }}>
            <Box sx={{ background: '#6366f1', color: '#fff', borderRadius: '12px', p: 1.1, display: 'flex' }}><MdDescription size={22} /></Box>
            <Box>
              <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18, color: '#0f172a', lineHeight: 1.2 }}>{tplDialog.editingId ? 'Edit template' : 'Add template'}</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>Register a template that is already approved in MSG91 / DLT.</Typography>
            </Box>
          </Box>
          <DialogContent sx={{ pt: '24px !important' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.3fr 1fr' }, gap: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <TextField fullWidth size="small" label="Template name" placeholder="e.g. Diwali offer 2026" required value={tplDialog.data.name} onChange={(e) => setTplField({ name: e.target.value })} error={!!tplDialog.errors.name} helperText={tplDialog.errors.name || 'Only you see this'} />
                <TextField fullWidth size="small" label="MSG91 template ID" required value={tplDialog.data.templateId} onChange={(e) => setTplField({ templateId: e.target.value })} error={!!tplDialog.errors.templateId} helperText={tplDialog.errors.templateId || 'Copy it from the MSG91 flow'} inputProps={{ style: { fontFamily: 'monospace' } }} />
                <TextField fullWidth size="small" label="Message text (for preview)" multiline minRows={4} placeholder="Hi ##name##, your order ##order_id## has shipped." value={tplDialog.data.content} onChange={(e) => setTplField({ content: e.target.value })} helperText="Paste the approved text. Write each variable as ##name##." />
                <Box>
                  <TextField fullWidth size="small" label="Variables" placeholder="name, order_id" value={tplDialog.data.variables} onChange={(e) => setTplField({ variables: e.target.value })} helperText="Separate with commas; these must match the MSG91 flow." />
                  {tplVars.length > 0 && tplDialog.data.variables.split(',').map((v) => v.trim()).filter(Boolean).join(',') !== tplVars.join(',') && (
                    <Button size="small" onClick={() => setTplField({ variables: tplVars.join(', ') })} sx={{ textTransform: 'none', fontWeight: 700, mt: 0.5 }}>Use variables found in the text: {tplVars.join(', ')}</Button>
                  )}
                </Box>
              </Box>
              <Box>
                <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.75 }}>Preview</Typography>
                <PhoneBubble text={tplDialog.data.content} empty="The message appears here as you type" />
                {tplDialog.data.content && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b', mt: 1 }}>{smsParts(tplDialog.data.content).length} characters</Typography>}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button onClick={() => setTplDialog((d) => ({ ...d, open: false }))} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569', borderRadius: '10px', px: 2.5 }}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={busy} sx={{ textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', px: 3.5, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { background: 'rgb(255 190 0)' } }}>{busy ? 'Saving…' : tplDialog.editingId ? 'Update template' : 'Save template'}</Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={!!tplDelete} onClose={() => !busy && setTplDelete(null)} PaperProps={{ sx: { borderRadius: '14px', p: 1, minWidth: 380 } }}>
        <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800 }}>Remove template?</DialogTitle>
        <DialogContent><Typography sx={{ fontFamily: FONT }}><b>{tplDelete?.name}</b> will no longer be offered for new campaigns. Past campaigns keep their history.</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setTplDelete(null)} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={removeTemplate} disabled={busy} variant="contained" sx={{ textTransform: 'none', fontWeight: 700, background: '#dc2626', '&:hover': { background: '#b91c1c' } }}>{busy ? 'Removing…' : 'Yes, remove'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SMS;
